import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { TILE_LAYERS } from '../../config';
import { normalizeLng } from '../../game/geo';
import { pinHtml, unwrapNear, type MapViewProps } from './types';

/** Number of failed tiles (without any success) before switching to the next tile server. */
const TILE_ERROR_THRESHOLD = 4;

function makeIcon(kind: 'guess' | 'actual', label?: string) {
  return L.divIcon({ className: 'map-pin-icon', html: pinHtml(kind, label), iconSize: [32, 42], iconAnchor: [16, 41] });
}

export default function LeafletMap({ initialView, guess, onPick, pairs, resizeKey, className, ariaLabel }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const guessMarkerRef = useRef<L.Marker | null>(null);
  const resultLayerRef = useRef<L.LayerGroup | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const [tilesFailed, setTilesFailed] = useState(false);

  // Create the map once.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const map = L.map(container, {
      center: [initialView.center.lat, initialView.center.lng],
      zoom: initialView.zoom,
      minZoom: 1,
      worldCopyJump: true,
      zoomControl: true,
      attributionControl: true,
    });
    map.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>');
    mapRef.current = map;

    // Tile servers with automatic fallback.
    let layerIndex = 0;
    let layer: L.TileLayer | null = null;
    const addLayer = () => {
      const cfg = TILE_LAYERS[layerIndex];
      let errors = 0;
      let loaded = false;
      layer = L.tileLayer(cfg.url, {
        attribution: cfg.attribution,
        maxZoom: cfg.maxZoom,
        subdomains: cfg.subdomains ?? 'abc',
        crossOrigin: true,
        detectRetina: false,
      });
      layer.on('tileload', () => {
        loaded = true;
        setTilesFailed(false);
      });
      layer.on('tileerror', () => {
        errors++;
        if (loaded || errors < TILE_ERROR_THRESHOLD) return;
        if (layerIndex < TILE_LAYERS.length - 1) {
          map.removeLayer(layer as L.TileLayer);
          layerIndex++;
          addLayer();
        } else {
          setTilesFailed(true);
        }
      });
      layer.addTo(map);
    };
    addLayer();

    map.on('click', (e: L.LeafletMouseEvent) => {
      const wrapped = e.latlng.wrap();
      onPickRef.current?.({ lat: wrapped.lat, lng: normalizeLng(wrapped.lng) });
    });

    const observer = new ResizeObserver(() => map.invalidateSize({ pan: false }));
    observer.observe(container);
    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      guessMarkerRef.current = null;
      resultLayerRef.current = null;
    };
    // The initial view is only used at creation.
  }, []);

  // Cursor feedback in interactive mode.
  useEffect(() => {
    containerRef.current?.classList.toggle('map-view--pickable', Boolean(onPick) && !pairs);
  }, [onPick, pairs]);

  // Player marker (interactive mode).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pairs || !guess) {
      guessMarkerRef.current?.remove();
      guessMarkerRef.current = null;
      return;
    }
    if (guessMarkerRef.current) {
      guessMarkerRef.current.setLatLng([guess.lat, guess.lng]);
    } else {
      guessMarkerRef.current = L.marker([guess.lat, guess.lng], { icon: makeIcon('guess'), keyboard: false }).addTo(map);
    }
  }, [guess, pairs]);

  // Result mode: markers, lines and fitted view.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    resultLayerRef.current?.remove();
    resultLayerRef.current = null;
    if (!pairs || pairs.length === 0) return;

    const group = L.layerGroup().addTo(map);
    const bounds = L.latLngBounds([]);
    for (const pair of pairs) {
      const actual = L.latLng(pair.actual.lat, pair.actual.lng);
      bounds.extend(actual);
      if (pair.guess) {
        const g = unwrapNear(pair.actual, pair.guess);
        const guessLatLng = L.latLng(g.lat, g.lng);
        bounds.extend(guessLatLng);
        L.polyline([guessLatLng, actual], { color: '#0b1020', weight: 3, opacity: 0.85, dashArray: '2 8', lineCap: 'round' }).addTo(group);
        L.marker(guessLatLng, { icon: makeIcon('guess', pair.label), keyboard: false }).addTo(group);
      }
      L.marker(actual, { icon: makeIcon('actual', pair.label), keyboard: false, zIndexOffset: 500 }).addTo(group);
    }
    resultLayerRef.current = group;
    map.invalidateSize({ pan: false });
    if (bounds.isValid()) {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      if (sw.equals(ne)) map.setView(sw, 14, { animate: false });
      else map.fitBounds(bounds, { padding: [48, 48], maxZoom: 16, animate: false });
    }
  }, [pairs]);

  useEffect(() => {
    mapRef.current?.invalidateSize({ pan: false });
  }, [resizeKey]);

  return (
    <div className={`map-view ${className ?? ''}`}>
      <div ref={containerRef} className="map-view__canvas" role="application" aria-label={ariaLabel} />
      {tilesFailed && (
        <div className="map-view__warning" role="status">
          Le fond de carte ne se charge pas. Vous pouvez quand même cliquer pour placer votre marqueur.
        </div>
      )}
    </div>
  );
}
